// R16b · the arithmetic behind Maya's thinking trace, kept out of the component so it can be run
// by `node --test` (web/tests/mayaTrace.test.js) instead of only by eye.
//
// Three rules from `trace_ui_research.md` "跨产品共性" live here and nowhere else:
//   2. 展开后不是平铺，是分组的两级链 — consecutive rows of one kind fold into one group head.
//   3. 模型思考默认隐藏，且降级成与工具行同级的一行 — every gap between tool calls is summed into
//      ONE `Thinking` row, and the reasoning text hides inside it.
//   5. 产物从思考链里抽走 — the artefact summaries leave the chain and become chips.
//
// The server never times the gaps between tool calls (it times the calls), so thinking time is
// derived by subtraction: whatever the turn took, minus the tools, minus writing the answer.

const GROUP_VERBS = { read: "Read", site: "Made", article: "Made", publish: "Sent", other: "Ran" };
const GROUP_NOUNS = {
  read: "lookups",
  site: "site changes",
  article: "article changes",
  publish: "publish requests",
  other: "steps",
};
// A chip is a door when there is a room behind it. Anything not listed stays a plain label.
const CHIP_VIEWS = {
  soarank_get_report: "reports",
  soarank_report_section: "reports",
  soarank_history_trend: "reports",
  soarank_rerun_diagnosis: "reports",
  soarank_plan_tasks: "plan",
  soarank_list_articles: "files",
  soarank_write_article: "files",
  soarank_edit_article: "files",
  soarank_list_uploads: "files",
  soarank_edit_site: "sites",
  soarank_restyle: "sites",
  soarank_set_color: "sites",
  soarank_set_structure: "sites",
  soarank_gen_image: "sites",
  soarank_set_brand_url: "sites",
  soarank_request_publish: "sites",
};

// Under ten seconds one decimal is information ("0.2s"); past it, it is noise.
export function secs(ms) {
  const value = Math.max(0, Number(ms) || 0) / 1000;
  return (value < 10 ? value.toFixed(1) : String(Math.round(value))) + "s";
}

export function rowLabel(row, settled) {
  if (!row) return "";
  if (!settled && row.state === "running") return row.label || "";
  return row.label_done || row.label || "";
}

// Consecutive calls to the SAME tool become one group.
//
// Kind alone is too coarse: nine read tools share `kind: "read"`, so folding on it turns "read the
// diagnosis, read two sections, check the trend, check the tasks" into one meaningless
// `Read 5 lookups`. The noun is too coarse the other way: every tool with no label falls back to
// the same noun ("steps"), so two DIFFERENT unknown tools folded into one group claiming they were
// the same work. `group_key` is the server's one-way digest of the tool name — it separates tools
// exactly, and the browser still cannot print which tools they were. A stored trace from before it
// existed falls back to the old pair, which is wrong only for unlabelled tools.
function groupKey(row) {
  if (row?.group_key) return "k:" + row.group_key;
  return String(row?.kind || "other") + "|" + String(row?.group_noun || "");
}

// Consecutive rows that share a key become one bucket. Split out of `groupTraceRows` because the
// live chain groups the same way while the rows are still arriving — a second call to a tool that
// is already on screen has to land INSIDE the group above it, not start a new row under it.
export function groupConsecutive(rows) {
  const out = [];
  for (const row of rows || []) {
    const key = groupKey(row);
    const last = out[out.length - 1];
    if (last && last.key === key) last.members.push(row);
    else out.push({ key, kind: String(row?.kind || "other"), members: [row] });
  }
  return out;
}

// The head a bucket of two or more rows gets: "Read 2 diagnoses".
function groupLabel(kind, members) {
  const nouns = new Set(members.map((row) => row.group_noun).filter(Boolean));
  const noun = nouns.size === 1 ? [...nouns][0] : GROUP_NOUNS[kind] || GROUP_NOUNS.other;
  return (GROUP_VERBS[kind] || GROUP_VERBS.other) + " " + members.length + " " + noun;
}

// A lone row keeps no shell at all ("Completed 1 step" is the thing every product in the survey
// went out of its way not to print).
export function groupTraceRows(rows, settled = true) {
  return groupConsecutive(rows).map((group, index) => {
    const { kind, members } = group;
    if (members.length < 2) {
      const row = members[0];
      return {
        type: "row",
        key: row.call_id || "row-" + index,
        kind,
        label: rowLabel(row, settled),
        error: row.state === "error" ? row.error || "" : "",
        ms: row.ms || 0,
      };
    }
    return {
      type: "group",
      key: "grp-" + (members[0].call_id || index),
      kind,
      label: groupLabel(kind, members),
      ms: members.reduce((total, row) => total + (row.ms || 0), 0),
      members: members.map((row, memberIndex) => ({
        type: "row",
        key: row.call_id || "m-" + index + "-" + memberIndex,
        kind,
        label: rowLabel(row, settled),
        error: row.state === "error" ? row.error || "" : "",
        ms: row.ms || 0,
      })),
    };
  });
}

// The gaps the server does not time: the whole turn, minus every tool call, minus the time spent
// streaming the answer. Never negative, and never reported when it rounds to nothing.
export function thinkingMs({ totalMs = 0, writeMs = 0, rows = [] } = {}) {
  const tools = (rows || []).reduce((total, row) => total + (row.ms || 0), 0);
  return Math.max(0, Math.round(totalMs - writeMs - tools));
}

// The chain the expanded trace renders: grouped tool work, then ONE thinking row, then the answer.
// `thinkMs` is the server's own measurement when the turn carried one (`final.trace_ms.thinking`);
// only a turn stored before that existed falls back to the browser's subtraction.
export function buildChain({ rows = [], totalMs = 0, writeMs = 0, reasoning = "", settled = true, thinkMs = null } = {}) {
  const chain = groupTraceRows(rows, settled);
  const think = Number.isFinite(thinkMs) && thinkMs !== null ? Math.max(0, Math.round(thinkMs)) : thinkingMs({ totalMs, writeMs, rows });
  if (think >= 100 || reasoning) chain.push({ type: "think", key: "think", ms: think, reasoning: reasoning || "" });
  if (writeMs > 0) chain.push({ type: "write", key: "write", ms: writeMs });
  return chain;
}

// ── the live chain ────────────────────────────────────────────────────────────────────────────
//
// PT, 2026-09-13: "它是回答完以后再重新写思考链。正常的流程应该是先想思考链，告诉你一步一步做什么,
// 最终给答案，所以现在这个顺序搞反了。" The chain above is the one a REPLAYED turn gets: it is built
// from durations, so it can only be built once they are all known. This one is built from the
// clock, so it can be built while the turn is still running — every row appears at the moment the
// frame that proves it arrives (`status` → `tool running` → `tool done` → first `delta` → `final`,
// which is the order the gateway really sends them; verified against a live next stream).
//
// The two differ in one visible way and it is the point: thinking here is a row per GAP, in its
// place in the timeline, ticking while it is the present; there it is one summed row at the end,
// because a stored trace carries no timestamps to put the gaps back in order with.
const THINK_MIN_MS = 100;

function startOf(row, fallback) {
  const at = Number(row?.startedAt);
  return Number.isFinite(at) && at > 0 ? at : fallback;
}
// A row that is still running ends "now"; a settled one ends at whatever the server timed it as,
// falling back to the browser's own clock when the row carries no duration.
function endOf(row, start, now) {
  if (row?.state === "running") return now;
  const ms = Number(row?.ms);
  return Number.isFinite(ms) && ms > 0 ? start + ms : start;
}
function liveRowItem(row, kind, start, end, index, memberIndex = null) {
  const timed = row?.state !== "running" && Number.isFinite(Number(row?.ms)) && Number(row.ms) > 0;
  return {
    type: "row",
    key: row?.call_id || (memberIndex === null ? "row-" + index : "m-" + index + "-" + memberIndex),
    kind,
    label: rowLabel(row, false),
    error: row?.state === "error" ? row.error || "" : "",
    ms: timed ? Number(row.ms) : Math.max(0, Math.round(end - start)),
    running: row?.state === "running",
  };
}

// `now` is the clock the chain is read against: `Date.now()` while the turn runs, and the instant
// the turn settled afterwards — reading Date.now() forever would keep "Wrote the answer" growing
// for as long as the customer leaves the tab open.
export function buildLiveChain({ rows = [], startedAt = 0, answerAt = 0, now = 0, settled = false, reasoning = "" } = {}) {
  const clock = now || Date.now();
  const chain = [];
  let cursor = startedAt || 0;
  let thinks = 0;
  const pushThink = (from, to, running) => {
    const ms = Math.max(0, Math.round(to - from));
    if (!running && ms < THINK_MIN_MS) return;
    chain.push({ type: "think", key: "think-" + thinks++, ms, running, reasoning: "" });
  };
  groupConsecutive(rows).forEach((group, index) => {
    const { kind, members } = group;
    let end = startOf(members[0], cursor || clock);
    const start = end;
    const items = members.map((row, memberIndex) => {
      const memberStart = startOf(row, end);
      const memberEnd = endOf(row, memberStart, clock);
      if (memberEnd > end) end = memberEnd;
      return liveRowItem(row, kind, memberStart, memberEnd, index, memberIndex);
    });
    if (cursor) pushThink(cursor, Math.max(cursor, start), false);
    if (items.length < 2) chain.push({ ...items[0], key: items[0].key || "row-" + index });
    else {
      chain.push({
        type: "group",
        key: "grp-" + (members[0].call_id || index),
        kind,
        label: groupLabel(kind, members),
        ms: items.reduce((total, item) => total + (item.ms || 0), 0),
        running: items.some((item) => item.running),
        members: items,
      });
    }
    cursor = Math.max(cursor, end);
  });
  if (answerAt) {
    if (cursor) pushThink(cursor, Math.max(cursor, answerAt), false);
    chain.push({ type: "write", key: "write", ms: Math.max(0, Math.round(clock - answerAt)), running: !settled });
  } else if (cursor && !(rows || []).some((row) => row?.state === "running")) {
    // Nothing is being written and no call is in flight, so the present moment IS the thinking.
    // (While a call runs, the present is that call — a "Thinking · 0.0s" row under it would be a
    // second progress control saying nothing.)
    pushThink(cursor, clock, !settled);
  }
  // The reasoning text belongs to the last stretch of thinking; if the turn had no measurable gap
  // at all it still needs a row to hide in.
  if (reasoning) {
    let last = null;
    for (const item of chain) if (item.type === "think") last = item;
    if (last) last.reasoning = reasoning;
    else {
      const write = chain.findIndex((item) => item.type === "write");
      const row = { type: "think", key: "think-" + thinks++, ms: 0, running: false, reasoning };
      if (write >= 0) chain.splice(write, 0, row);
      else chain.push(row);
    }
  }
  return chain;
}

// Artefacts, deduplicated, in the order Maya produced them. A chip is the same object shape a user
// attachment gets — that equivalence is the whole point of lifting them out of the chain.
export function traceChips(rows) {
  const seen = new Set();
  const chips = [];
  for (const row of rows || []) {
    const label = String(row?.summary || "").trim();
    if (!label || row.state === "error" || seen.has(label)) continue;
    seen.add(label);
    chips.push({ label, view: CHIP_VIEWS[String(row.name || "")] || "" });
  }
  return chips;
}

// The one line the header shows. `null` means: render nothing at all — a settled turn that used no
// tools must not leave "Thought for 3s · 0 lookups" behind (production audit §C.6: most turns).
// `copy` carries the two translated sentences; the defaults are the English the product ships.
export function traceHeading({ rows = [], live = false, liveLabel = "", writing = false, totalMs = 0, copy = {}, chainOpen = false } = {}) {
  if (live) {
    // With the chain open underneath, the current step is already on screen as its own row; a
    // header repeating it word for word reads like a rendering bug. The header then says which
    // PHASE the turn is in and lets the rows say what she is doing.
    if (!chainOpen) {
      const running = (rows || []).find((row) => row.state === "running");
      if (running) return running.label || liveLabel;
    }
    if (writing) return copy.writing || "Writing the answer";
    return liveLabel;
  }
  const lookups = (rows || []).length;
  if (!lookups) return null;
  const unit = lookups === 1 ? copy.lookup || "lookup" : copy.lookups || "lookups";
  // No duration is better than a wrong one: a turn with no timing at all (an old stored trace whose
  // rows carry no ms) would otherwise read "Thought for 0.0s", which is not a fact about anything.
  if (!totalMs) return String(lookups) + " " + unit;
  return (copy.thought || "Thought for {s} · {n} {unit}")
    .replace("{s}", secs(totalMs))
    .replace("{n}", String(lookups))
    .replace("{unit}", unit);
}
